import React, { useState, useEffect } from 'react';
import { Route, Routes, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import {
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  ClockIcon,
  CreditCardIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  LanguageIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

import { 
  GeneralSettings, 
  NotificationSettings, 
  SecuritySettings, 
  BusinessHours,
  BillingSettings,
  CourseSettings,
  ClassroomSettings,
  TrainerSettings,
  CertificationSettings
} from '../../components/settings';

const SettingsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname.split('/').pop() || 'general');
  const [settings, setSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  // Fetch current settings
  const { data: settingsData } = useQuery('organizationSettings', async () => {
    try {
      const response = await axios.get('/api/settings');
      return response.data;
    } catch (error) {
      // Fallback to localStorage if API fails
      const localSettings = localStorage.getItem('organizationSettings');
      return localSettings ? JSON.parse(localSettings) : {};
    }
  });

  useEffect(() => {
    if (settingsData) {
      setSettings(settingsData);
    }
  }, [settingsData]);

  useEffect(() => {
    const seg = location.pathname.split('/').pop() || 'general';
    setActiveTab(seg);
  }, [location.pathname]);

  // Handle settings changes
  const handleSettingsChange = (newSettings) => {
    setSettings(newSettings);
    setHasChanges(true);
    // Auto-save to localStorage
    localStorage.setItem('organizationSettings', JSON.stringify(newSettings));
    
    // Invalidate React Query cache to refresh all components using settings
    queryClient.invalidateQueries('settings');
    queryClient.invalidateQueries('organizationSettings');
    
    // Try to save to API
    axios.put('/api/settings', newSettings).catch(error => {
      console.warn('Failed to save to API, saved locally:', error);
    });
  };

  // Handle changes flag
  const handleHasChanges = (changed) => {
    setHasChanges(changed);
  };

  const tabs = [
    { id: 'general', name: 'Général', icon: Cog6ToothIcon, component: GeneralSettings },
    { id: 'notifications', name: 'Notifications', icon: BellIcon, component: NotificationSettings },
    { id: 'security', name: 'Sécurité', icon: ShieldCheckIcon, component: SecuritySettings },
    { id: 'hours', name: 'Horaires', icon: ClockIcon, component: BusinessHours },
    { id: 'billing', name: 'Facturation', icon: CreditCardIcon, component: BillingSettings },
    { id: 'courses', name: 'Cours', icon: AcademicCapIcon, component: CourseSettings },
    { id: 'classrooms', name: 'Salles', icon: BuildingLibraryIcon, component: ClassroomSettings },
    { id: 'trainers', name: 'Formateurs', icon: UserGroupIcon, component: TrainerSettings },
    { id: 'certifications', name: 'Certificats', icon: DocumentTextIcon, component: CertificationSettings },
  ];

  return (
    <div className="min-h-[80vh] space-y-6">
      <PageHeader
        title="Paramètres"
        description="Configuration de l'établissement, des cours, des salles et de la facturation."
      />
      <div className="flex flex-col gap-6 md:flex-row">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="md:w-64 flex-shrink-0"
        >
          <UiCard className="sticky top-20">
            <h2 className="mb-4 px-1 text-xl font-semibold text-slate-900 dark:text-slate-50">Sections</h2>
            <div className="flex flex-col space-y-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.id}
                  to={`/settings/${tab.id}`}
                  className={clsx(
                    'flex items-center rounded-lg px-3 py-2 transition-colors',
                    activeTab === tab.id
                      ? 'bg-forma-600 text-white shadow-md shadow-forma-600/20'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/40 dark:hover:text-slate-50'
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon className="mr-3 h-5 w-5" />
                  <span>{tab.name}</span>
                </Link>
              ))}
            </div>

            <div className="mt-8 border-t border-slate-200 pt-4 dark:border-slate-600">
              <h3 className="mb-2 px-3 text-sm font-medium text-slate-600 dark:text-slate-400">Ressources</h3>
              <div className="flex flex-col space-y-1">
                <Link
                  to="#"
                  className="flex items-center rounded-lg px-3 py-2 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/40 dark:hover:text-slate-50"
                >
                  <DevicePhoneMobileIcon className="mr-3 h-5 w-5" />
                  <span>Application mobile</span>
                </Link>
                <Link
                  to="#"
                  className="flex items-center rounded-lg px-3 py-2 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/40 dark:hover:text-slate-50"
                >
                  <LanguageIcon className="mr-3 h-5 w-5" />
                  <span>Langues</span>
                </Link>
                <Link
                  to="#"
                  className="flex items-center rounded-lg px-3 py-2 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800/40 dark:hover:text-slate-50"
                >
                  <DocumentTextIcon className="mr-3 h-5 w-5" />
                  <span>Guide d'utilisation</span>
                </Link>
              </div>
            </div>

            <div className="mt-8 px-1">
              <button type="button" className="w-full rounded-lg bg-slate-100 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800/40 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-50">
                Contacter le support
              </button>
            </div>
          </UiCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="min-w-0 flex-1"
        >
          <UiCard padding={false} className="overflow-hidden">
            <Routes>
              <Route 
                path="/" 
                element={
                  <GeneralSettings 
                    settings={settings}
                    onSettingsChange={handleSettingsChange}
                    onHasChanges={handleHasChanges}
                  />
                } 
              />
              <Route 
                path="/general" 
                element={
                  <GeneralSettings 
                    settings={settings}
                    onSettingsChange={handleSettingsChange}
                    onHasChanges={handleHasChanges}
                  />
                } 
              />
              <Route path="/notifications" element={<NotificationSettings />} />
              <Route path="/security" element={<SecuritySettings />} />
              <Route path="/hours" element={<BusinessHours />} />
              <Route path="/billing" element={<BillingSettings />} />
              <Route path="/courses" element={<CourseSettings />} />
              <Route path="/classrooms" element={<ClassroomSettings />} />
              <Route 
                path="/trainers" 
                element={
                  <TrainerSettings 
                    settings={settings}
                    onSettingsChange={handleSettingsChange}
                    onHasChanges={handleHasChanges}
                  />
                } 
              />
              <Route path="/certifications" element={<CertificationSettings />} />
            </Routes>
          </UiCard>
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage;
